import React, { useState, useEffect } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useParams, useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Plus, 
  Send, 
  ShoppingCart,
  Edit2,
  Check,
  X,
  FileText,
  CheckCircle,
  Clock,
  Package
} from 'lucide-react';
import { useInquiryItems } from '../../hooks/useInquiryLists';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';
import { formatCurrency } from '../../utils/currency';
import InquiryListItem from './InquiryListItem';
import UserLayout from './UserLayout';
import PageHeader from '../shared/PageHeader';
import Card from '../shared/Card';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import StatusBadge, { inquiryStatusTone } from '../shared/StatusBadge';
import { SkeletonRows } from '../shared/Skeleton';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  quoted: 'Quoted',
  converted: 'Converted',
  cancelled: 'Cancelled',
};

// Banner per status; colours follow the inquiry tone map in shared/StatusBadge.
const STATUS_BANNERS = {
  draft: {
    icon: FileText,
    title: 'Draft Inquiry',
    text: 'Add items to your inquiry list and submit it when ready for staff review.',
    box: 'bg-gray-50 border-gray-200', iconColor: 'text-gray-500', titleColor: 'text-gray-900', textColor: 'text-gray-600',
  },
  submitted: {
    icon: Clock,
    title: 'Submitted for Review',
    text: 'Your inquiry has been submitted. Staff will review and provide a quote soon.',
    box: 'bg-blue-50 border-blue-200', iconColor: 'text-blue-600', titleColor: 'text-blue-900', textColor: 'text-blue-700',
  },
  quoted: {
    icon: CheckCircle,
    title: 'Quote Received',
    text: 'Staff has provided quotes for all items. Review and convert to cart to place an order.',
    box: 'bg-green-50 border-green-200', iconColor: 'text-green-600', titleColor: 'text-green-900', textColor: 'text-green-700',
  },
  converted: {
    icon: Package,
    title: 'Converted to Cart',
    text: 'This inquiry has been converted to a cart order.',
    box: 'bg-gray-50 border-gray-200', iconColor: 'text-gray-500', titleColor: 'text-gray-900', textColor: 'text-gray-600',
  },
};

const InquiryListDetail = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const { id } = useParams();
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [inquiryList, setInquiryList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newItem, setNewItem] = useState({
    partNumber: '',
    description: '',
    quantity: 1,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get user
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);

  const { items, addItem, updateItem, removeItem } = useInquiryItems(id, user?.id);

  // Fetch inquiry list details
  useEffect(() => {
    const fetchInquiryList = async () => {
      try {
        const { data, error } = await supabase
          .from('inquiry_lists')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setInquiryList(data);
        setEditedName(data.list_name);
        setEditedDescription(data.description || '');
      } catch (error) {
        console.error('Error fetching inquiry list:', error);
        showError('Failed to load inquiry list');
        history.push('/user/inquiry-lists');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInquiryList();
    }
  }, [id, history]);

  // Update inquiry list info
  const handleUpdateInfo = async () => {
    try {
      const { error } = await supabase
        .from('inquiry_lists')
        .update({
          list_name: editedName,
          description: editedDescription || null
        })
        .eq('id', id);

      if (error) throw error;

      setInquiryList({
        ...inquiryList,
        list_name: editedName,
        description: editedDescription
      });
      setEditingInfo(false);
      showNotification('Inquiry list updated');
    } catch (error) {
      showError('Failed to update inquiry list: ' + error.message);
    }
  };

  // Add item to inquiry
  const handleAddItem = async () => {
    if (!newItem.partNumber.trim() || !newItem.description.trim()) {
      showError('Part number and description are required');
      return;
    }

    try {
      await addItem(
        newItem.partNumber,
        newItem.description,
        newItem.quantity,
        newItem.notes || null
      );
      
      showNotification('Item added to inquiry');
      setShowAddItemModal(false);
      setNewItem({
        partNumber: '',
        description: '',
        quantity: 1,
        notes: ''
      });
    } catch (error) {
      showError('Failed to add item: ' + error.message);
    }
  };

  // Submit inquiry for review
  const handleSubmitInquiry = async () => {
    if (items.length === 0) {
      showError('Cannot submit an empty inquiry list');
      return;
    }

    if (!window.confirm('Submit this inquiry for staff review? You will not be able to edit it after submission.')) {
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase
        .rpc('submit_inquiry', {
          inquiry_list_id: id,
          user_uuid: user.id
        });

      if (error) throw error;

      // Send notification to staff
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${supabase.supabaseUrl}/functions/v1/send-inquiry-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ inquiryListId: id }),
          }
        );

        if (!response.ok) {
          console.error('Failed to send staff notification:', await response.text());
        }
      } catch (notifError) {
        console.error('Error sending staff notification:', notifError);
        // Don't fail the inquiry submission if notification fails
      }

      showNotification('Inquiry submitted successfully! Staff will review and provide a quote.');
      history.push('/user/inquiry-lists');
    } catch (error) {
      showError('Failed to submit inquiry: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Convert quoted inquiry to cart
  const handleConvertToCart = async () => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    try {
      const { data, error } = await supabase
        .rpc('convert_inquiry_to_cart', {
          inquiry_list_id: id,
          user_uuid: user.id
        });

      if (error) throw error;

      // Get existing cart from localStorage
      const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');

      // Add inquiry items to cart
      const newCartItems = data.map(item => ({
        partNumber: item.part_number,
        description: item.description,
        quantity: item.quantity,
        price: parseFloat(item.price),
        total: item.quantity * parseFloat(item.price)
      }));

      // Merge with existing cart
      const updatedCart = [...existingCart, ...newCartItems];
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      notifyCartUpdated();

      showNotification(`Added ${newCartItems.length} items to cart`);
      history.push('/cart');
    } catch (error) {
      showError('Failed to convert to cart: ' + error.message);
    }
  };

  // Calculate total quoted price
  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      if (item.quoted_price) {
        return sum + (item.quantity * parseFloat(item.quoted_price));
      }
      return sum;
    }, 0);
  };

  const hasAllQuotedPrices = items.length > 0 && items.every(item => item.quoted_price !== null);
  const totalQuoted = calculateTotal();

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => history.push('/user/inquiry-lists')}
      icon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
    >
      Back to Inquiry Lists
    </Button>
  );

  if (loading) {
    return (
      <UserLayout>
        <div className="space-y-6">
          {backButton}
          <SkeletonRows rows={4} />
        </div>
      </UserLayout>
    );
  }

  if (!inquiryList) {
    return null;
  }

  const isDraft = inquiryList.status === 'draft';
  const isQuoted = inquiryList.status === 'quoted';
  const banner = STATUS_BANNERS[inquiryList.status];
  const BannerIcon = banner?.icon;

  const resetNewItem = () => setNewItem({
    partNumber: '',
    description: '',
    quantity: 1,
    notes: ''
  });

  return (
    <UserLayout>
      <div className="space-y-6">
      <div className="space-y-4">
        {backButton}

        {editingInfo ? (
          <Card>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-list-name" className="block text-sm font-medium text-gray-700 mb-1">
                  List Name
                </label>
                <input
                  id="edit-list-name"
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="edit-list-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="edit-list-description"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateInfo} icon={<Check className="w-4 h-4" aria-hidden="true" />}>
                  Save
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditingInfo(false);
                    setEditedName(inquiryList.list_name);
                    setEditedDescription(inquiryList.description || '');
                  }}
                  icon={<X className="w-4 h-4" aria-hidden="true" />}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <PageHeader
            title={inquiryList.list_name}
            description={inquiryList.description}
            meta={
              <StatusBadge tone={inquiryStatusTone(inquiryList.status)}>
                {STATUS_LABELS[inquiryList.status] || inquiryList.status}
              </StatusBadge>
            }
            actions={
              <>
                {isDraft && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => setEditingInfo(true)}
                      ariaLabel="Edit list details"
                      icon={<Edit2 className="w-4 h-4" aria-hidden="true" />}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => setShowAddItemModal(true)}
                      icon={<Plus className="w-4 h-4" aria-hidden="true" />}
                    >
                      Add Item
                    </Button>
                    {items.length > 0 && (
                      <Button
                        onClick={handleSubmitInquiry}
                        loading={submitting}
                        icon={<Send className="w-4 h-4" aria-hidden="true" />}
                      >
                        {submitting ? 'Submitting...' : 'Submit for Review'}
                      </Button>
                    )}
                  </>
                )}
                {isQuoted && hasAllQuotedPrices && orderingEnabled && (
                  <Button
                    onClick={handleConvertToCart}
                    icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
                  >
                    Convert to Cart
                  </Button>
                )}
              </>
            }
          />
        )}
      </div>

      {/* Status Banner */}
      {banner && (
        <div className={`border rounded-lg p-4 flex items-start gap-3 ${banner.box}`}>
          <BannerIcon className={`flex-shrink-0 mt-0.5 ${banner.iconColor}`} size={20} aria-hidden="true" />
          <div className="flex-1">
            <div className={`font-medium ${banner.titleColor}`}>{banner.title}</div>
            <div className={`text-sm ${banner.textColor}`}>{banner.text}</div>
          </div>
          {isQuoted && hasAllQuotedPrices && (
            <div className="text-right">
              <div className="text-sm text-green-700">Total Quote</div>
              <div className="text-2xl font-bold text-green-900 tabular-nums">
                {formatCurrency(totalQuoted)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Items List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Items ({items.length})
        </h2>

        {items.length === 0 ? (
          <Card padding="none">
            <EmptyState
              icon={<Package className="h-12 w-12" />}
              title="No items yet"
              description="Add items to your inquiry list to get started"
              action={isDraft && (
                <Button onClick={() => setShowAddItemModal(true)} icon={<Plus className="w-4 h-4" aria-hidden="true" />}>
                  Add First Item
                </Button>
              )}
            />
          </Card>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {items.map(item => (
                <InquiryListItem
                  key={item.id}
                  item={item}
                  onUpdate={updateItem}
                  onRemove={removeItem}
                  isEditable={isDraft}
                  showQuotedPrice={isQuoted}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      <AnimatePresence>
        {showAddItemModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddItemModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-item-title"
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full"
            >
              <h2 id="add-item-title" className="text-lg font-semibold text-gray-900 mb-4">Add Item to Inquiry</h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="new-item-part" className="block text-sm font-medium text-gray-700 mb-1">
                    Part Number *
                  </label>
                  <input
                    id="new-item-part"
                    type="text"
                    value={newItem.partNumber}
                    onChange={(e) => setNewItem({ ...newItem, partNumber: e.target.value })}
                    placeholder="e.g., ABC123"
                    className={inputClass}
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="new-item-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    id="new-item-description"
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Describe the part..."
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div>
                  <label htmlFor="new-item-quantity" className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    id="new-item-quantity"
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="new-item-notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    id="new-item-notes"
                    value={newItem.notes}
                    onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                    placeholder="Any special requirements or notes..."
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowAddItemModal(false);
                    resetNewItem();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddItem}
                  disabled={!newItem.partNumber.trim() || !newItem.description.trim()}
                  className="flex-1"
                >
                  Add Item
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </UserLayout>
  );
};

export default InquiryListDetail;
